import React, { MouseEvent, KeyboardEvent } from "react";
import styles from "./machines-cancel-service-technician-modal.module.scss";
import { Button } from "@src/components/atoms/buttons/button/button";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { IndicatorText } from "@src/components/atoms/indicator-text/indicator-text";
import companyIcon from "@src/assets/svg/icon-building.svg";
import dbIcon from "@src/assets/svg/source-icon.svg";
import closeIcon from "@src/assets/svg/close-icon.svg";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { IconButton } from "@src/components/atoms/buttons/icon-button/icon-button";
import { MachinesListSupportSessionsCancelRequestModalTextTypes } from "@common/types/machines-list-types";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";

const cssClassName = "machines-cancel-service-technician-modal";

interface MachinesCancelServiceTechnicianManagerModalPropsTypes {
  onConfirm: (e: MouseEvent | KeyboardEvent) => void;
  onCancel: (e: MouseEvent | KeyboardEvent) => void;
  machineName: string;
  companyName: string;
  isConnected: boolean;
  modalTexts: MachinesListSupportSessionsCancelRequestModalTextTypes;
  loading: boolean;
  disableConfirmAction?: boolean;
  disabledConfirmTooltip?: string;
}

export const MachinesCancelServiceTechnicianManagerModal = ({
  onConfirm,
  onCancel,
  machineName,
  companyName,
  isConnected,
  modalTexts: texts,
  loading,
  disableConfirmAction = false,
  disabledConfirmTooltip = "",
}: MachinesCancelServiceTechnicianManagerModalPropsTypes) => {
  const handleBackdropClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel(e);
    }
  };

  const handleBackdropKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      if (e.target === e.currentTarget) {
        onCancel(e);
      }
    }
  };

  return (
    <div
      className={styles[cssClassName]}
      role="button"
      tabIndex={0}
      aria-label="Close modal"
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
    >
      <div className={styles[`${cssClassName}__container`]}>
        <div className={styles[`${cssClassName}__header`]}>
          <h2 className={styles[`${cssClassName}__title`]}>{texts.title}</h2>
          <div className={styles[`${cssClassName}__close-btn`]}>
            <IconButton fun={onCancel} icon={closeIcon} />
          </div>
        </div>

        <HorizontalSeparator verticalMargin={16} />

        <p className={styles[`${cssClassName}__message`]}>{texts.message}</p>

        <div className={styles[`${cssClassName}__info-card`]}>
          <div className={styles[`${cssClassName}__info-row`]}>
            <img
              className={styles[`${cssClassName}__icon-company`]}
              src={companyIcon}
              alt="Company"
            />
            <span className={styles[`${cssClassName}__info-text`]}>
              {companyName}
            </span>
          </div>
          <span className={styles[`${cssClassName}__divider`]}>/</span>
          <div className={styles[`${cssClassName}__info-row`]}>
            <IndicatorText text="" variant={isConnected ? "green" : "red"} />

            <span className={styles[`${cssClassName}__info-text`]}>
              {machineName}
            </span>
            <img
              className={styles[`${cssClassName}__icon-db`]}
              src={dbIcon}
              alt="Machine"
            />
          </div>
        </div>

        <HorizontalSeparator verticalMargin={16} />

        <div className={styles[`${cssClassName}__actions`]}>
          <FlexRowContainer horizontalGap={12}>
            <ButtonWithLoader
              fun={onConfirm}
              text={texts.confirmButton}
              variant={"dark-orange"}
              loading={loading}
              disabled={disableConfirmAction}
              tooltip={disabledConfirmTooltip}
              style={disableConfirmAction ? { opacity: 0.4 } : undefined}
            />
            <Button
              fun={onCancel}
              text={texts.cancelButton}
              variant="secondary"
            />
          </FlexRowContainer>
        </div>
      </div>
    </div>
  );
};
